import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { fontStretch } from "src/design/typography";
import Brand from "src/grading/components/Brand/Brand";
import { HeroDecoration } from "src/grading/components/HeroBand/HeroBand";

export interface EntryFrameProps {
  children: ReactNode;
  /**
   * Renders the page's single `<h1>` above the children. Do not render your
   * own h1 in `children` when `title` is set.
   */
  title?: string;
  /** Overline above the title. */
  eyebrow?: string;
  /** Max width of the form column content, in px. */
  maxWidth?: number;
}

const EVENT_ICONS = ["situps", "plank", "mile", "beep", "pushups", "pullups"];

/** Eyebrow above entry-page titles: 11/800 expanded, red ink, .12em tracking. */
export const EntryEyebrow = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="overline"
    component="p"
    sx={{ m: 0, color: "dangerInk", letterSpacing: ".12em", lineHeight: 1 }}
  >
    {children}
  </Typography>
);

/** Typography for entry-page titles: 32/900 expanded, sentence case. */
export const entryTitleSx = {
  m: 0,
  fontSize: { xs: 28, sm: 32 },
  fontWeight: 900,
  fontStretch: fontStretch.display,
  lineHeight: 1.05,
  letterSpacing: "-.01em",
  textTransform: "none",
} as const;

/**
 * Split layout for sign-in and invitation pages (design `isLogin`): the form column with the
 * brand at the top and the not-affiliated note at the bottom, and the hero panel with diagonal bands,
 * dots and the "Your whole class. One period." headline.
 */
export const EntryFrame = ({
  children,
  title,
  eyebrow,
  maxWidth = 400,
}: EntryFrameProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: "1fr",
        minHeight: "100vh",
        backgroundColor: theme.palette.tokens.surface,
        [theme.breakpoints.up(900)]: {
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)",
        },
      })}
    >
      <Box
        component="main"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: 3.5,
          backgroundColor: theme.palette.tokens.surface,
          px: { xs: 3, md: 6 },
          py: { xs: 3, md: 4.5 },
        })}
      >
        <Brand variant="full" />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2.75,
            width: "100%",
            maxWidth,
            mx: "auto",
          }}
        >
          {eyebrow || title ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {eyebrow ? <EntryEyebrow>{eyebrow}</EntryEyebrow> : null}
              {title ? (
                <Typography variant="h1" sx={entryTitleSx}>
                  {title}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          <Box>{children}</Box>
        </Box>
        <Typography
          sx={(theme) => ({
            maxWidth: 460,
            fontSize: 11.5,
            lineHeight: 1.5,
            color: theme.palette.tokens.textMuted,
          })}
        >
          {t("app.notAffiliated")}
        </Typography>
      </Box>
      <Box
        aria-hidden
        data-print-hide
        sx={(theme) => ({
          display: "none",
          position: "relative",
          overflow: "hidden",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: 6,
          backgroundColor: theme.palette.hero,
          color: theme.palette.heroInk,
          [theme.breakpoints.up(900)]: { display: "flex" },
        })}
      >
        <HeroDecoration
          bandWidth="55%"
          bandHeight="58%"
          clip="polygon(30% 0, 100% 0, 100% 100%)"
          period={30}
          dotSize={26}
        />
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 2.75,
            maxWidth: 560,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {EVENT_ICONS.map((name) => (
              <Box
                key={name}
                component="span"
                sx={(theme) => ({
                  display: "inline-block",
                  width: 40,
                  height: 40,
                  backgroundColor: theme.palette.heroInk,
                  WebkitMask: `url(/assets/events/${name}.svg) center / contain no-repeat`,
                  mask: `url(/assets/events/${name}.svg) center / contain no-repeat`,
                })}
              />
            ))}
          </Box>
          <Typography
            sx={{
              m: 0,
              fontSize: 46,
              fontWeight: 900,
              fontStretch: fontStretch.display,
              textTransform: "uppercase",
              letterSpacing: "-.01em",
              lineHeight: 0.95,
            }}
          >
            {t("entry.headline", {
              defaultValue: "Your whole class. One period.",
            })}
          </Typography>
          <Typography
            sx={(theme) => ({
              fontSize: 16,
              lineHeight: 1.5,
              color: theme.palette.heroMuted,
            })}
          >
            {t("entry.blurb", {
              defaultValue:
                "Plan the session, run three stations with volunteers, then finalize. Certificates and family letters are ready before the bell.",
            })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EntryFrame;
